import { useState } from "react";
import { Smartphone, Monitor, Download, Share, Plus, ChevronRight, CheckCircle2, ArrowDown, MoreVertical, Square } from "lucide-react";

export default function InstallPWA() {
  const [platform, setPlatform] = useState("android");

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a3d3d] via-[#0d5454] to-[#0a3d3d] text-white" data-testid="install-pwa-page">
      {/* Header */}
      <header className="pt-8 pb-4 text-center px-4">
        <div className="inline-flex items-center gap-2 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center border border-white/20">
            <img src="/icon-192.png" alt="Hana" className="w-10 h-10 rounded-lg" />
          </div>
          <div className="text-left">
            <h1 className="text-2xl font-bold tracking-tight">Hana</h1>
            <p className="text-emerald-300 text-sm">Telesuivi Oncologique</p>
          </div>
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold max-w-md mx-auto leading-snug" data-testid="install-title">
          Installez Hana sur votre telephone
        </h2>
        <p className="text-emerald-200/80 mt-2 text-sm sm:text-base max-w-sm mx-auto">
          Accedez a votre suivi en un clic, sans passer par l'App Store
        </p>
      </header>

      {/* Platform Selector */}
      <div className="flex justify-center gap-3 px-4 mt-4 mb-8" data-testid="platform-selector">
        <button
          onClick={() => setPlatform("android")}
          data-testid="platform-android-btn"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
            platform === "android"
              ? "bg-white text-[#0a3d3d] shadow-lg shadow-black/20"
              : "bg-white/10 text-white/80 hover:bg-white/20 border border-white/10"
          }`}
        >
          <AndroidIcon />
          Android
        </button>
        <button
          onClick={() => setPlatform("ios")}
          data-testid="platform-ios-btn"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
            platform === "ios"
              ? "bg-white text-[#0a3d3d] shadow-lg shadow-black/20"
              : "bg-white/10 text-white/80 hover:bg-white/20 border border-white/10"
          }`}
        >
          <AppleIcon />
          iPhone / iPad
        </button>
      </div>

      {/* Steps */}
      <div className="max-w-md mx-auto px-4 pb-16 space-y-5">
        {platform === "android" ? <AndroidSteps /> : <IOSSteps />}

        {/* Final note */}
        <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-5 text-center backdrop-blur-sm">
          <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="font-medium text-emerald-100">
            L'application Hana apparaitra sur votre ecran d'accueil !
          </p>
          <p className="text-emerald-200/60 text-sm mt-1">
            Ouvrez-la comme n'importe quelle application
          </p>
        </div>

        {/* Direct access link */}
        <div className="text-center pt-4">
          <a
            href="/"
            data-testid="go-to-app-btn"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-3 rounded-full transition-all shadow-lg shadow-emerald-500/30"
          >
            <Monitor className="w-4 h-4" />
            Acceder a Hana maintenant
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, icon, title, description, visual }) {
  return (
    <div className="bg-white/[0.07] border border-white/10 rounded-2xl p-5 backdrop-blur-sm" data-testid={`install-step-${step}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm border border-emerald-500/30">
          {step}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-base">{title}</h3>
          <p className="text-emerald-200/70 text-sm mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      {visual && (
        <div className="mt-4 bg-[#0a2e2e] rounded-xl p-4 border border-white/5">
          {visual}
        </div>
      )}
    </div>
  );
}

function AndroidSteps() {
  return (
    <>
      <StepCard
        step={1}
        title="Ouvrez Chrome"
        description="Allez sur www.hana.ma avec Google Chrome"
        visual={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <ChromeIcon />
            </div>
            <div className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm text-emerald-200/80 font-mono">
              www.hana.ma
            </div>
          </div>
        }
      />
      <StepCard
        step={2}
        title='Appuyez sur les 3 points'
        description="En haut a droite du navigateur, appuyez sur le menu (les trois points)"
        visual={
          <div className="flex items-center justify-end">
            <div className="bg-white/10 rounded-lg p-2 border-2 border-emerald-400/50 animate-pulse">
              <MoreVertical className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
        }
      />
      <StepCard
        step={3}
        title='"Ajouter a l&apos;ecran d&apos;accueil"'
        description='Cherchez et appuyez sur "Ajouter a l&apos;ecran d&apos;accueil" dans le menu'
        visual={
          <div className="space-y-1.5">
            <MenuItem icon={<Share className="w-4 h-4" />} label="Partager..." />
            <MenuItem icon={<Download className="w-4 h-4" />} label="Telecharger" />
            <MenuItem icon={<Plus className="w-4 h-4" />} label="Ajouter a l'ecran d'accueil" highlight />
          </div>
        }
      />
      <StepCard
        step={4}
        title="Confirmez l'installation"
        description='Appuyez sur "Ajouter" pour confirmer. Hana s&apos;installe instantanement !'
        visual={
          <div className="flex justify-center gap-4">
            <button className="px-6 py-2 rounded-lg bg-white/10 text-white/50 text-sm">Annuler</button>
            <button className="px-6 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium shadow-lg">Ajouter</button>
          </div>
        }
      />
    </>
  );
}

function IOSSteps() {
  return (
    <>
      <StepCard
        step={1}
        title="Ouvrez Safari"
        description="Allez sur www.hana.ma avec Safari (navigateur par defaut Apple)"
        visual={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <SafariIcon />
            </div>
            <div className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm text-emerald-200/80 font-mono">
              www.hana.ma
            </div>
          </div>
        }
      />
      <StepCard
        step={2}
        title="Appuyez sur Partager"
        description="En bas de l'ecran, appuyez sur l'icone de partage (carre avec fleche vers le haut)"
        visual={
          <div className="flex items-center justify-center">
            <div className="bg-white/10 rounded-lg p-3 border-2 border-emerald-400/50 animate-pulse">
              <ShareIOSIcon />
            </div>
          </div>
        }
      />
      <StepCard
        step={3}
        title='"Sur l&apos;ecran d&apos;accueil"'
        description='Faites defiler vers le bas et appuyez sur "Sur l&apos;ecran d&apos;accueil"'
        visual={
          <div className="space-y-1.5">
            <MenuItem icon={<Copy className="w-4 h-4" />} label="Copier le lien" />
            <MenuItem icon={<Plus className="w-4 h-4" />} label="Sur l'ecran d'accueil" highlight />
            <MenuItem icon={<Square className="w-4 h-4" />} label="Ajouter aux favoris" />
          </div>
        }
      />
      <StepCard
        step={4}
        title="Confirmez"
        description='Appuyez sur "Ajouter" en haut a droite. C&apos;est fait !'
        visual={
          <div className="flex items-center justify-between px-2">
            <span className="text-blue-400 text-sm">Annuler</span>
            <span className="font-semibold text-white text-sm">Sur l'ecran d'accueil</span>
            <span className="text-blue-400 text-sm font-semibold">Ajouter</span>
          </div>
        }
      />
    </>
  );
}

function MenuItem({ icon, label, highlight }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
      highlight ? "bg-emerald-500/20 border border-emerald-500/30" : "bg-white/5"
    }`}>
      <span className={highlight ? "text-emerald-400" : "text-white/40"}>{icon}</span>
      <span className={`text-sm ${highlight ? "text-emerald-300 font-medium" : "text-white/60"}`}>{label}</span>
      {highlight && <ArrowDown className="w-3 h-3 text-emerald-400 ml-auto animate-bounce" />}
    </div>
  );
}

function Copy({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function AndroidIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48A5.84 5.84 0 0012 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31A5.983 5.983 0 006 7h12c0-2.12-1.1-3.98-2.77-5.04l.3-.8zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function ChromeIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" className="text-emerald-400" />
      <circle cx="12" cy="12" r="4" fill="currentColor" className="text-emerald-400" />
    </svg>
  );
}

function SafariIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" className="text-blue-400" />
      <path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" fill="currentColor" className="text-blue-400" />
    </svg>
  );
}

function ShareIOSIcon() {
  return (
    <svg className="w-6 h-6 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
